import { Vector3 } from 'three';
import { BALANCE } from '../config/balance';
import type { CollisionWorld } from '../world/CollisionWorld';
import type { Drone } from '../enemies/Drone';

const scratchStep = new Vector3();
const scratchToDrone = new Vector3();

export type GrappleState = 'idle' | 'flying' | 'attached';

/**
 * A magnetic grapple hook.
 *
 * Right-click launches the hook; it travels at a fixed speed, attaches to the
 * first magnetic surface or magnetic drone it reaches, and then reels the dog
 * in at a constant speed. Non-magnetic drones are ignored.
 */
export class GrappleHook {
  private state: GrappleState = 'idle';
  private cooldown = 0;
  private travelled = 0;
  private targetDrone: Drone | null = null;
  private readonly position = new Vector3();
  private readonly direction = new Vector3();
  readonly anchor = new Vector3();

  constructor(private readonly collision: CollisionWorld) {}

  get status(): GrappleState {
    return this.state;
  }

  get isAttached(): boolean {
    return this.state === 'attached';
  }

  get isFlying(): boolean {
    return this.state === 'flying';
  }

  get attachedDrone(): Drone | null {
    return this.state === 'attached' ? this.targetDrone : null;
  }

  /**
   * Where the hook itself is right now: the flying tip, or the anchor it is
   * fastened to. The rope is drawn from the muzzle to this point.
   */
  get tip(): Vector3 {
    if (this.state === 'flying') return this.position;
    // A hooked drone is reeled in, so the line has to follow the drone rather
    // than stay pinned where the hook first caught it.
    if (this.targetDrone) return this.targetDrone.position;
    return this.anchor;
  }

  reset(): void {
    this.state = 'idle';
    this.cooldown = 0;
    this.travelled = 0;
    this.targetDrone = null;
  }

  update(dt: number): void {
    this.cooldown = Math.max(0, this.cooldown - dt);
  }

  /** Launch the hook. Returns true when it actually fired. */
  fire(origin: Vector3, direction: Vector3): boolean {
    if (this.state !== 'idle' || this.cooldown > 0) return false;
    this.cooldown = BALANCE.tools.grappleCooldown;
    this.state = 'flying';
    this.travelled = 0;
    this.targetDrone = null;
    this.position.copy(origin);
    this.direction.copy(direction).normalize();
    return true;
  }

  /**
   * Advance a flying hook. Attaches to the world, or to the first magnetic
   * drone whose shell it crosses.
   */
  step(dt: number, drones: readonly Drone[]): void {
    if (this.state !== 'flying') return;
    const step = BALANCE.tools.grappleSpeed * dt;
    if (step <= 0) return;

    // A surface exactly at the end of the step must still register, so the
    // ray gets a hair more distance than the hook moves.
    const hit = this.collision.raycast(this.position, this.direction, step + 1e-3);
    if (hit) {
      this.anchor.copy(hit.point);
      this.state = 'attached';
      this.targetDrone = null;
      return;
    }

    scratchStep.copy(this.position).addScaledVector(this.direction, step);
    for (const drone of drones) {
      if (drone.isDestroyed || !drone.magnetic) continue;
      scratchToDrone.subVectors(drone.position, scratchStep);
      if (scratchToDrone.lengthSq() <= (drone.radius + 0.2) * (drone.radius + 0.2)) {
        this.anchor.copy(drone.position);
        this.state = 'attached';
        this.targetDrone = drone;
        return;
      }
    }

    this.position.copy(scratchStep);
    this.travelled += step;
    if (this.travelled >= BALANCE.tools.grappleRange) this.release();
  }

  release(): void {
    this.state = 'idle';
    this.targetDrone = null;
    this.travelled = 0;
  }
}
